import React, { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label.tsx';
import { Switch } from '@/components/ui/switch';
import { Plus, X, HelpCircle, Calendar as CalendarIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { ProjectPayload } from '@shared/interfaces/projectPayload.interface';
import { ProjectYearlyCosts } from '@shared/interfaces/projectYearlyCosts.interface';
import FinancialMetricsTable from './FinancialMetricsTable';

interface Collaborator { name: string; email: string }

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  newProject: ProjectPayload & { includePricingInValueAssessment: boolean };
  setNewProject: Dispatch<SetStateAction<ProjectPayload & { includePricingInValueAssessment: boolean }>>;
  onSubmit: () => void;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const salesforceIdRegex = /^[A-Za-z0-9]{18}$/;

const NewProjectModal: React.FC<NewProjectModalProps> = ({
  isOpen,
  onClose,
  newProject,
  setNewProject,
  onSubmit,
}) => {
  const [collaborators, setCollaborators] = useState<Collaborator[]>(newProject.client_collaborators || []);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // keep newProject.client_collaborators in sync
  const setCollabs = (next: Collaborator[]) => {
    setCollaborators(next);
    setNewProject({ ...newProject, client_collaborators: next });
  };

  const addCollaborator = () => setCollabs([...(collaborators || []), { name: '', email: '' }]);
  const removeCollaborator = (i: number) => setCollabs((collaborators || []).filter((_, idx) => idx !== i));
  const updateCollaborator = (i: number, key: keyof Collaborator, value: string) => {
    const next = [...(collaborators || [])];
    next[i] = { ...next[i], [key]: value } as Collaborator;
    setCollabs(next);
  };

  // derived validation
  const salesforceOk = !!newProject.salesforce_opportunity_id && salesforceIdRegex.test(newProject.salesforce_opportunity_id);
  const clientOrgOk = !!(newProject.client_org || '').trim();
  const primaryNameOk = !!(newProject.primary_contact_name || '').trim();
  const primaryEmail = (newProject.primary_contact_email || '').trim() || (newProject.assessment_contact_email || '').trim();
  const primaryEmailOk = !!primaryEmail && emailRegex.test(primaryEmail);
  const ndaSelected = newProject.nda_status === 'already_signed' || newProject.nda_status === 'needs_nda';
  const collabsOk = (collaborators || []).every(c => c.name.trim() && emailRegex.test(c.email));

  const handleYearlyCostsChange = useCallback((costs: ProjectYearlyCosts) => {
    setNewProject(prev => ({
      ...prev,
      yearly_costs: costs,
    }));
  }, [setNewProject]);

  const isCreateDisabled = useMemo(() => {
    return !newProject.name?.trim() || !clientOrgOk || !primaryNameOk || !primaryEmailOk || !salesforceOk || !ndaSelected || !collabsOk;
  }, [newProject.name, clientOrgOk, primaryNameOk, primaryEmailOk, salesforceOk, ndaSelected, collabsOk]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[1120px] bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Project</DialogTitle>
          <DialogDescription>
            Create a new R2R assessment project to get started.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* 0. Company Name */}
          <div className="grid gap-2">
            <Label htmlFor="new-client" className="text-gray-700 dark:text-gray-300">
              Company Name <span className="text-red-500">*</span>
              </Label>
            <Input
              id="new-client"
              value={newProject.client_org || ''}
              onChange={(e) => setNewProject({ ...newProject, client_org: e.target.value })}
              placeholder="Company name"
              className={`border-gray-200 dark:border-gray-600 ${touched.client_org && !clientOrgOk ? 'border-red-500' : ''}`}
               data-cy="companyName"
              onBlur={() => setTouched({ ...touched, client_org: true })}
            />
            {touched.client_org && !clientOrgOk && (
              <p className="text-sm text-red-500">Company Name is required</p>
            )}
          </div>
          {/* 1. Project Name */}
          <div className="grid gap-2">
            <Label htmlFor="new-name" className="text-gray-700 dark:text-gray-300">
              Project Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="new-name"
              value={newProject.name || ''}
              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
              placeholder="Enter company name"
              className="border-gray-200 dark:border-gray-600"
              data-cy="projectName"
              onBlur={() => setTouched({ ...touched, name: true })}
            />
          </div>

          {/* 2. Description */}
          <div className="grid gap-2">
            <Label htmlFor="new-description" className="text-gray-700 dark:text-gray-300">
              Description <span className="text-sm text-gray-500">(0-500 chars)</span>
            </Label>
            <Textarea
              id="new-description"
              value={newProject.description || ''}
              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
              placeholder="Describe the project objectives and scope"
              className="border-gray-200 dark:border-gray-600 min-h-[100px]"
              maxLength={500}
              data-cy="projectDescription"
            />
            <div className="text-xs text-gray-500 text-right">{(newProject.description || '').length}/500</div>
          </div>

          {/* 3. Primary Contact – Full Name */}
          <div className="grid gap-2">
            <Label htmlFor="new-contact-name" className="text-gray-700 dark:text-gray-300">
              Primary Contact – Full Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="new-contact-name"
              value={newProject.primary_contact_name || ''}
              onChange={(e) => setNewProject({ ...newProject, primary_contact_name: e.target.value })}
              placeholder="Enter primary contact name"
              className={`border-gray-200 dark:border-gray-600 ${touched.primary_contact_name && !primaryNameOk ? 'border-red-500' : ''}`}
              onBlur={() => setTouched({ ...touched, primary_contact_name: true })}
            />
            {touched.primary_contact_name && !primaryNameOk && (
              <p className="text-sm text-red-500">Primary contact name is required</p>
            )}
          </div>

          {/* 4. Primary Contact – Email */}
          <div className="grid gap-2">
            <Label htmlFor="new-contact-email" className="text-gray-700 dark:text-gray-300">
              Primary Contact – Email <span className="text-red-500">*</span>
            </Label>
            <Input
              id="new-contact-email"
              type="email"
              value={newProject.primary_contact_email || newProject.assessment_contact_email || ''}
              onChange={(e) => setNewProject({ ...newProject, primary_contact_email: e.target.value, assessment_contact_email: e.target.value })}
              placeholder="Enter primary contact email"
              className={`border-gray-200 dark:border-gray-600 ${touched.primary_contact_email && !primaryEmailOk ? 'border-red-500' : ''}`}
              onBlur={() => setTouched({ ...touched, primary_contact_email: true })}
            />
            {touched.primary_contact_email && !primaryEmailOk && (
              <p className="text-sm text-red-500">Invalid email format</p>
            )}
          </div>

          {/* 5. Company Collaborators */}
          <div className="grid gap-2">
            <div className="flex items-center justify-between">
              <Label className="text-gray-700 dark:text-gray-300">Company Collaborators</Label>
              <Button type="button" variant="outline" size="sm" onClick={addCollaborator} className="text-xs">
                <Plus className="w-3 h-3 mr-1" /> Add Collaborator
              </Button>
            </div>
            {collaborators.map((collab, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-2 p-3 border border-gray-200 dark:border-gray-600 rounded-md">
                <div>
                  <Input
                    placeholder="Name *"
                    value={collab.name}
                    onChange={(e) => updateCollaborator(index, 'name', e.target.value)}
                    className={`text-sm ${touched[`collab_${index}_name`] && !collab.name.trim() ? 'border-red-500' : ''}`}
                    onBlur={() => setTouched({ ...touched, [`collab_${index}_name`]: true })}
                  />
                  {touched[`collab_${index}_name`] && !collab.name.trim() && (
                    <p className="text-xs text-red-500 mt-1">Name is required</p>
                  )}
                </div>
                <div>
                  <Input
                    placeholder="Email *"
                    type="email"
                    value={collab.email}
                    onChange={(e) => updateCollaborator(index, 'email', e.target.value)}
                    className={`text-sm ${touched[`collab_${index}_email`] && !emailRegex.test(collab.email) ? 'border-red-500' : ''}`}
                    onBlur={() => setTouched({ ...touched, [`collab_${index}_email`]: true })}
                  />
                  {touched[`collab_${index}_email`] && !emailRegex.test(collab.email) && (
                    <p className="text-xs text-red-500 mt-1">Invalid email format</p>
                  )}
                </div>
                <div className="flex items-center">
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeCollaborator(index)} className="text-red-500 hover:text-red-700 px-2">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* 6. Salesforce Opportunity ID */}
          <div className="grid gap-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="new-salesforce-id" className="text-gray-700 dark:text-gray-300">
                Salesforce Opportunity ID <span className="text-red-500">*</span>
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <div className="text-sm space-y-1">
                      <p className="font-medium">To find your Salesforce Opportunity ID:</p>
                      <p>• In Salesforce Lightning: Open the Opportunity → the URL looks like /lightning/r/Opportunity/{'{ID}'}/view. Copy the {'{ID}'} (18 characters).</p>
                      <p>• Paste the ID here. Letters and numbers only.</p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id="new-salesforce-id"
              value={newProject.salesforce_opportunity_id || ''}
              onChange={(e) => setNewProject({ ...newProject, salesforce_opportunity_id: e.target.value })}
              placeholder="Enter 18-character Salesforce Opportunity ID"
              className={`border-gray-200 dark:border-gray-600 ${touched.salesforce && !salesforceOk ? 'border-red-500' : ''}`}
              maxLength={18}
              onBlur={() => setTouched({ ...touched, salesforce: true })}
            />
            {touched.salesforce && !salesforceOk && (
              <p className="text-sm text-red-500">Must be exactly 18 alphanumeric characters (A-Z, a-z, 0-9)</p>
            )}
          </div>

          {/* 7. NDA Status */}
          <div className="grid gap-3">
            <Label className="text-gray-700 dark:text-gray-300">
              NDA Status <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant={newProject.nda_status === 'already_signed' ? 'default' : 'outline'}
                onClick={() => setNewProject({ ...newProject, nda_status: 'already_signed' })}
                className="text-sm"
              >
                Already signed
              </Button>
              <Button
                type="button"
                variant={newProject.nda_status === 'needs_nda' ? 'default' : 'outline'}
                onClick={() => setNewProject({ ...newProject, nda_status: 'needs_nda' })}
                className="text-sm"
              >
                Needs NDA
              </Button>
            </div>
            {!ndaSelected && touched.nda && (
              <p className="text-sm text-red-500">NDA status selection is required</p>
            )}

            {/* NDA Reference/Link and Date (shown when "Already signed" is selected) */}
            {newProject.nda_status === 'already_signed' && (
              <div className="grid gap-3 ml-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
                <div className="grid gap-2">
                  <Label htmlFor="nda-reference" className="text-sm text-gray-600 dark:text-gray-400">
                    NDA Reference / Link <span className="text-xs text-gray-500">(optional)</span>
                  </Label>
                  <Input
                    id="nda-reference"
                    value={newProject.nda_reference || ''}
                    onChange={(e) => setNewProject({ ...newProject, nda_reference: e.target.value })}
                    placeholder="Enter NDA reference or link"
                    className="text-sm"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="nda-signed-date" className="text-sm text-gray-600 dark:text-gray-400">
                    NDA Signed Date <span className="text-xs text-gray-500">(optional)</span>
                  </Label>
                  <Input
                    id="nda-signed-date"
                    type="date"
                    value={newProject.nda_signed_date || ''}
                    onChange={(e) => setNewProject({ ...newProject, nda_signed_date: e.target.value })}
                    className="text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* 8. Export Options */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">Export Options</Label>
              <p className="text-sm text-gray-500">Enable export functionality for this project</p>
            </div>
            <Switch
              checked={newProject.export_options_enabled ?? true}
              onCheckedChange={(checked) => setNewProject({ ...newProject, export_options_enabled: checked })}
            />
          </div>

          {/* 9. Show Financial Metrics */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="showFinancialMetrics" className="text-gray-700 dark:text-gray-300">Show financial metrics</Label>
                <p className="text-sm text-gray-500">When on, the Visualization Summary can display financial metrics</p>
              </div>
              <Switch
                id="showFinancialMetrics"
                checked={!!newProject.show_financial_metrics}
                onCheckedChange={(checked) => setNewProject({ ...newProject, show_financial_metrics: checked, includePricingInValueAssessment: checked })}
              />
            </div>

            <FinancialMetricsTable value={newProject.yearly_costs} onChange={handleYearlyCostsChange} />
          </div>

          {/* 10. Key Dates */}
          <div className="grid gap-4">
            <Label className="text-gray-700 dark:text-gray-300 text-base font-medium">Key Dates</Label>
            {/* Schedule Date */}
            <div className="grid gap-2">
              <Label className="text-gray-700 dark:text-gray-300 text-sm">Schedule Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn('justify-start text-left font-normal', !newProject.assessment_scheduled_at && 'text-muted-foreground')}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newProject.assessment_scheduled_at
                      ? format(new Date(newProject.assessment_scheduled_at), 'PPP')
                      : 'Select schedule date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={newProject.assessment_scheduled_at ? new Date(newProject.assessment_scheduled_at) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                        setNewProject({ ...newProject, assessment_scheduled_at: utcDate.toISOString() });
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Completed Date (Read-only) */}
            <div className="grid gap-2">
              <Label className="text-gray-700 dark:text-gray-300 text-sm">Completed Date</Label>
              <Input
                value={newProject.assessment_completed_at ? format(new Date(newProject.assessment_completed_at), 'PPP') : '—'}
                readOnly
                className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                placeholder="Auto-stamped when assessment completes"
              />
            </div>

            {/* Presentation Date */}
            <div className="grid gap-2">
              <Label className="text-gray-700 dark:text-gray-300 text-sm">Presentation Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn('justify-start text-left font-normal', !newProject.presentation_scheduled_at && 'text-muted-foreground')}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {newProject.presentation_scheduled_at
                      ? format(new Date(newProject.presentation_scheduled_at), 'PPP')
                      : 'Select presentation date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={newProject.presentation_scheduled_at ? new Date(newProject.presentation_scheduled_at) : undefined}
                    onSelect={(date) => {
                      if (date) {
                        const utcDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
                        setNewProject({ ...newProject, presentation_scheduled_at: utcDate.toISOString() });
                      }
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            type="button"
            onClick={() => {
            // mark required fields as touched on the submitted attempt
              if (!ndaSelected) setTouched(prev => ({ ...prev, nda: true }));
              if (!touched.salesforce) setTouched(prev => ({ ...prev, salesforce: true }));
              if (!touched.primary_contact_email) setTouched(prev => ({ ...prev, primary_contact_email: true }));
              if (!touched.primary_contact_name) setTouched(prev => ({ ...prev, primary_contact_name: true }));
              if (!touched.name) setTouched(prev => ({ ...prev, name: true }));
              if (!touched.client_org) setTouched(prev => ({ ...prev, client_org: true }));
              if (!isCreateDisabled) onSubmit();
            }}
            disabled={isCreateDisabled}
            data-cy="createProjectButton"
          >
            Create Project
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewProjectModal;
